import { gql, useQuery } from '@apollo/client';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { useAppConfig } from '../../fetching/api/appConfig';

export const SCHEDULE_CHANGE_REQUEST_INDICATOR_QUERY = gql`
    query ScheduleChangeRequestIndicatorQuery($oneDayFromNow: DateTime!) {
        scheduleChangeRequests {
            allRequests: requestsToCreateShiftBlock(
                filters: { mayHaveFutureOccurrences: { exact: true } }
            ) {
                totalCount
            }
            urgentRequests: requestsToCreateShiftBlock(
                filters: {
                    startDatetime: { lt: $oneDayFromNow }
                    mayHaveFutureOccurrences: { exact: true }
                }
            ) {
                totalCount
            }
        }
    }
`;

// Queries for schedule change requests that are pending review by the currently logged in user.
// Returns counts of all requests and those requests which are considered urgent because their start
// date is within the next 24 hours.
export const useScheduleChangeRequestCounts = () => {
    const {
        scheduling: { manage_shift_block_requests: manageShiftBlockRequests },
    } = useAppConfig();
    const [oneDayFromNow, setOneDayFromNow] = useState(moment().add(1, 'day').toDate());

    useEffect(() => {
        if (!manageShiftBlockRequests) return;

        // Update the oneDayFromNow state every 5 minutes
        const timer = setTimeout(() => {
            setOneDayFromNow(moment().add(1, 'day').toDate());
        }, 1000 * 60 * 5);

        return () => clearTimeout(timer);
    }, [manageShiftBlockRequests, setOneDayFromNow]);

    const { data } = useQuery<
        {
            scheduleChangeRequests: {
                allRequests: {
                    totalCount: number;
                };
                urgentRequests: {
                    totalCount: number;
                };
            };
        },
        { oneDayFromNow: Date }
    >(SCHEDULE_CHANGE_REQUEST_INDICATOR_QUERY, {
        variables: {
            oneDayFromNow,
        },
        skip: !manageShiftBlockRequests,
    });

    return {
        allRequestsCount: data?.scheduleChangeRequests.allRequests.totalCount ?? 0,
        urgentRequestsCount: data?.scheduleChangeRequests.urgentRequests.totalCount ?? 0,
    };
};
